import { Link } from 'react-router-dom'

export default function Requirements() {
  return (
    <div className="py-16 px-6">
      <div className="max-w-3xl mx-auto">
        <h1 className="text-3xl font-semibold text-text-primary mb-4">
          What You Need
        </h1>
        <p className="text-lg text-text-secondary mb-12">
          Stuff you already have. Nothing to buy. Nothing to install.
        </p>

        {/* Requirements */}
        <section className="mb-12">
          <h2 className="text-xl font-semibold text-text-primary mb-4">
            Requirements
          </h2>
          <div className="space-y-4">
            <div className="card">
              <div className="flex items-start gap-4">
                <span className="text-2xl font-mono text-text-secondary">@</span>
                <div>
                  <h3 className="font-medium text-text-primary mb-1">
                    Google Account
                  </h3>
                  <p className="text-sm text-text-secondary">
                    Any Google account with Drive access. Personal accounts work fine. 
                    No Workspace required. Your account is both your login and your storage.
                  </p>
                </div>
              </div>
            </div>

            <div className="card">
              <div className="flex items-start gap-4">
                <span className="text-2xl font-mono text-text-secondary">&lt;/&gt;</span>
                <div>
                  <h3 className="font-medium text-text-primary mb-1">
                    Modern browser
                  </h3>
                  <p className="text-sm text-text-secondary">
                    Chrome, Firefox, Safari, or Edge from the last couple of years. 
                    The app runs entirely in your browser.
                  </p>
                </div>
              </div>
            </div>

            <div className="card">
              <div className="flex items-start gap-4">
                <span className="text-2xl font-mono text-text-secondary">~</span>
                <div>
                  <h3 className="font-medium text-text-primary mb-1">
                    Internet connection
                  </h3>
                  <p className="text-sm text-text-secondary">
                    Needed to load the app and sync to Drive. Works offline too, 
                    once loaded. Data syncs when you reconnect.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* What you don't need */}
        <section className="mb-12">
          <h2 className="text-xl font-semibold text-text-primary mb-4">
            What you don't need
          </h2>
          <div className="card bg-[#F5F5F3]">
            <ul className="space-y-3 text-sm text-text-secondary">
              <li className="flex items-center gap-2">
                <span className="text-error">x</span>
                <span>No account creation. No sign-up form. No email harvesting.</span>
              </li>
              <li className="flex items-center gap-2">
                <span className="text-error">x</span>
                <span>No software to download. No installer. No updates to manage.</span>
              </li>
              <li className="flex items-center gap-2">
                <span className="text-error">x</span>
                <span>No subscription. No payment. No credit card. No "free trial".</span>
              </li>
              <li className="flex items-center gap-2">
                <span className="text-error">x</span>
                <span>No IT department. No admin approval. No procurement process.</span>
              </li>
              <li className="flex items-center gap-2">
                <span className="text-error">x</span>
                <span>No Google Workspace. Personal Gmail works perfectly.</span>
              </li>
            </ul>
          </div>
        </section>

        {/* Storage */}
        <section className="mb-12">
          <h2 className="text-xl font-semibold text-text-primary mb-4">
            Storage usage
          </h2>
          <div className="card">
            <p className="text-sm text-text-secondary mb-4">
              Your data is JSON text. Tiny. Readable. Yours.
            </p>
            <ul className="space-y-2 text-sm text-text-secondary">
              <li>
                <strong className="text-text-primary">Typical usage:</strong>{' '}
                Under 1MB for hundreds of contacts, companies, and deals
              </li>
              <li>
                <strong className="text-text-primary">Google's free tier:</strong>{' '}
                15GB across Drive, Gmail, and Photos. You'll use almost none of it.
              </li>
              <li>
                <strong className="text-text-primary">Heavy use:</strong>{' '}
                Still probably a few megabytes. Text is small.
              </li>
            </ul>
          </div>
        </section>

        {/* Getting started */}
        <section className="mb-12">
          <h2 className="text-xl font-semibold text-text-primary mb-4">
            Getting started
          </h2>
          <div className="card">
            <ol className="space-y-4 text-sm text-text-secondary">
              <li className="flex items-start gap-3">
                <span className="bg-action-primary text-white rounded-full w-6 h-6 flex items-center justify-center text-xs font-medium shrink-0">
                  1
                </span>
                <div>
                  <strong className="text-text-primary">Click "Connect Google Drive"</strong>
                  <p className="mt-1">You'll be redirected to Google's sign-in page.</p>
                </div>
              </li>
              <li className="flex items-start gap-3">
                <span className="bg-action-primary text-white rounded-full w-6 h-6 flex items-center justify-center text-xs font-medium shrink-0">
                  2
                </span>
                <div>
                  <strong className="text-text-primary">Grant Drive file access</strong>
                  <p className="mt-1">
                    Google will ask you to approve the{' '}
                    <code className="bg-[#F5F5F3] px-1 py-0.5 rounded text-xs font-mono">
                      drive.file
                    </code>{' '}
                    permission. This lets Praxis create and access only its own files.
                  </p>
                </div>
              </li>
              <li className="flex items-start gap-3">
                <span className="bg-action-primary text-white rounded-full w-6 h-6 flex items-center justify-center text-xs font-medium shrink-0">
                  3
                </span>
                <div>
                  <strong className="text-text-primary">Start using Praxis</strong>
                  <p className="mt-1">
                    The app creates a PraxisCRM_Data folder in your Drive and you're 
                    ready to add companies, contacts, and deals.
                  </p>
                </div>
              </li>
            </ol>
          </div>
        </section>

        {/* Browser support */}
        <section className="mb-12">
          <h2 className="text-xl font-semibold text-text-primary mb-4">
            Browser support
          </h2>
          <div className="card">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
              <div>
                <p className="text-2xl mb-1">Chrome</p>
                <p className="text-sm text-text-secondary">90+</p>
              </div>
              <div>
                <p className="text-2xl mb-1">Firefox</p>
                <p className="text-sm text-text-secondary">88+</p>
              </div>
              <div>
                <p className="text-2xl mb-1">Safari</p>
                <p className="text-sm text-text-secondary">14+</p>
              </div>
              <div>
                <p className="text-2xl mb-1">Edge</p>
                <p className="text-sm text-text-secondary">90+</p>
              </div>
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="text-center pt-8 border-t border-border">
          <p className="text-text-secondary mb-4">
            Google account + browser = ready. That's it.
          </p>
          <Link to="/login" className="btn btn-primary">
            Connect Google Drive
          </Link>
        </section>
      </div>
    </div>
  )
}
